import React, { useState } from 'react';
import { type School, type User, type AssignedCourse, getUsers, getSchoolAssignedCourses, getUserAssignedCourses, deleteLessonUserPermissions, updateLessonUserPermissions } from '../../lib/api';
import { Trash2, Eye, Edit3, RefreshCw } from 'lucide-react';

type PermissionType = 'view_only' | 'edit_develop';

const EntityInspector: React.FC<{ schools: School[] }> = ({ schools }) => {
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [assignedCourses, setAssignedCourses] = useState<AssignedCourse[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingLessonId, setDeletingLessonId] = useState<number | null>(null);
  const [updatingLessonId, setUpdatingLessonId] = useState<number | null>(null);

  const handleSchoolChange = async (sId: number) => {
    setSelectedSchoolId(sId);
    setSelectedUserId(null);
    setAssignedCourses([]);
    if (sId) {
      const [u, c] = await Promise.all([
        getUsers({ school_id: sId, limit: 1000 }),
        getSchoolAssignedCourses(sId)
      ]);
      setUsers(u.data);
      setAssignedCourses(c.data);
    }
  };

  const handleUserChange = async (uId: number) => {
    setSelectedUserId(uId);
    if (uId) {
      const c = await getUserAssignedCourses(uId);
      setAssignedCourses(c.data);
    } else if (selectedSchoolId) {
      const c = await getSchoolAssignedCourses(selectedSchoolId);
      setAssignedCourses(c.data);
    }
  };

  const handleRefresh = async () => {
    if (!selectedUserId) return;
    setLoading(true);
    try {
      const c = await getUserAssignedCourses(selectedUserId);
      setAssignedCourses(c.data);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePermission = async (lessonId: number) => {
    if (!selectedUserId) return;
    if (!confirm('确定要删除该用户对此课时的权限吗？')) return;
    
    setDeletingLessonId(lessonId);
    try {
      await deleteLessonUserPermissions(lessonId, selectedUserId);
      await handleRefresh();
    } catch (error) {
      console.error('删除权限失败:', error);
      alert('删除权限失败');
    } finally {
      setDeletingLessonId(null);
    }
  };

  const handleTogglePermission = async (lessonId: number, currentType: PermissionType) => {
    if (!selectedUserId) return;
    
    const newType: PermissionType = currentType === 'view_only' ? 'edit_develop' : 'view_only';
    
    setUpdatingLessonId(lessonId);
    try {
      await updateLessonUserPermissions(lessonId, {
        user_id: selectedUserId,
        permission_type: newType
      });
      await handleRefresh();
    } catch (error) {
      console.error('更新权限失败:', error);
      alert('更新权限失败');
    } finally {
      setUpdatingLessonId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[calc(100vh-150px)]">
      <h2 className="text-lg font-bold text-gray-800 mb-6 border-b pb-2">权限反向查询</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">选择学校</label>
          <select 
            className="w-full border p-2 rounded" 
            value={selectedSchoolId || ''}
            onChange={e => handleSchoolChange(Number(e.target.value))}
          >
            <option value="">-- 请选择 --</option>
            {schools.filter(s => !s.is_deleted).map(s => (
              <option key={s.id} value={s.id}>{s.schoolname}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">选择用户 (可选)</label>
          <div className="flex gap-2">
            <select 
              className="flex-1 border p-2 rounded disabled:bg-gray-100" 
              disabled={!selectedSchoolId}
              value={selectedUserId || ''}
              onChange={e => handleUserChange(Number(e.target.value))}
            >
              <option value="">-- 查看整个学校 --</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.username} ({u.role}) {u.memo ? `- ${u.memo}` : ''}</option>)}
            </select>
            {selectedUserId && (
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50 transition-colors"
                title="刷新数据"
              >
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              </button>
            )}
          </div>
        </div>
      </div>

      {selectedUserId && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          💡 提示：选择用户后，可以对每个课时进行删除权限或切换权限类型操作
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-6 border border-gray-100">
        {assignedCourses.length === 0 ? <div className="text-gray-400">暂无数据</div> : (
          <div className="space-y-4">
            {assignedCourses.map(course => {
              const sortedStages = course.stages
                ?.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                .filter((stage, index, self) => 
                  index === self.findIndex(s => s.stage_id === stage.stage_id)
                );
              
              return (
                <div key={course.course_id} className="border rounded-lg p-4 bg-white shadow-sm">
                  <h3 className="font-bold text-lg text-gray-800">📚 {course.course_title}</h3>
                  <div className="ml-4 mt-2 border-l-2 pl-4 space-y-3">
                    {sortedStages?.map(stage => {
                      const sortedLessons = stage.lessons
                        ?.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                        .filter((lesson, index, self) => 
                          index === self.findIndex(l => l.lesson_id === lesson.lesson_id)
                        );
                      
                      return (
                        <div key={stage.stage_id}>
                          <h4 className="font-medium text-gray-700">🔹 {stage.sort_order || 0}. {stage.stage_title}</h4>
                          <div className="ml-4 mt-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {sortedLessons?.map(lesson => (
                              <div key={lesson.lesson_id} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm gap-2">
                                <span className="truncate flex-1">📄 {lesson.sort_order || 0}. {lesson.lesson_title}</span>
                                
                                <div className="flex items-center gap-2">
                                  <PermissionToggle
                                    permissionType={lesson.permission_type}
                                    disabled={!selectedUserId || updatingLessonId === lesson.lesson_id}
                                    onToggle={() => handleTogglePermission(lesson.lesson_id, lesson.permission_type)}
                                    loading={updatingLessonId === lesson.lesson_id}
                                  />
                                  
                                  {selectedUserId && (
                                    <button
                                      onClick={() => handleDeletePermission(lesson.lesson_id)}
                                      disabled={deletingLessonId === lesson.lesson_id}
                                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                      title="删除权限"
                                    >
                                      {deletingLessonId === lesson.lesson_id ? (
                                        <RefreshCw size={16} className="animate-spin" />
                                      ) : (
                                        <Trash2 size={16} />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

interface PermissionToggleProps {
  permissionType: PermissionType;
  disabled: boolean;
  onToggle: () => void;
  loading?: boolean;
}

const PermissionToggle: React.FC<PermissionToggleProps> = ({ permissionType, disabled, onToggle, loading }) => {
  const isViewOnly = permissionType === 'view_only';
  const isEditDevelop = permissionType === 'edit_develop';

  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`
        flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-all
        ${isViewOnly 
          ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' 
          : 'bg-green-100 text-green-700 hover:bg-green-200'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      title={disabled ? '请先选择用户' : `点击切换为${isViewOnly ? '编辑权限' : '只读权限'}`}
    >
      {loading ? (
        <RefreshCw size={12} className="animate-spin" />
      ) : isViewOnly ? (
        <Eye size={12} />
      ) : (
        <Edit3 size={12} />
      )}
      <span>{isViewOnly ? '只读' : '编辑'}</span>
    </button>
  );
};

export default EntityInspector;
